import { FC, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Typography,
  Box,
  Autocomplete,
} from "@mui/material";
import { apiClient } from "../../utils/apiClient";
import { useConfirmationDialog } from "../../components/ConfirmationDialog";
import { MonacoJsonEditor } from "../../components/MonacoJsonEditor";

interface WorkflowType {
  name: string;
  description?: string;
  testData: Array<{
    name: string;
    description?: string;
    data: any;
  }>;
}

interface CreateRecurringWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  workflowTypes: WorkflowType[];
  isOneTime?: boolean;
}

const CreateRecurringWorkflowModal: FC<CreateRecurringWorkflowModalProps> = ({
  open,
  onClose,
  onSuccess,
  workflowTypes,
  isOneTime = false,
}) => {
  const { openAlert, ConfirmationDialog } = useConfirmationDialog();
  
  // Form state
  const [selectedWorkflowType, setSelectedWorkflowType] = useState("");
  const [selectedTestDataIndex, setSelectedTestDataIndex] = useState<number>(0);
  const [schedule, setSchedule] = useState("@every 1h");
  const [waitForPrevious, setWaitForPrevious] = useState(true);
  const [initialParams, setInitialParams] = useState("{}");
  const [startTime, setStartTime] = useState<string>("");
  const [startDelay, setStartDelay] = useState<number>(0);
  const [phaseSec, setPhaseSec] = useState<number>(0);
  const [timingMode, setTimingMode] = useState<'none' | 'startTime' | 'startDelay' | 'phaseSec'>('none');

  const handleCreate = async () => {
    try {
      let params = {};
      try {
        params = JSON.parse(initialParams);
      } catch (error) {
        openAlert(
          "Invalid JSON in initial parameters",
          { title: "Error", confirmColor: "error" }
        );
        return;
      }

      // Prepare API payload
      const payload: any = {
        workflowName: selectedWorkflowType,
        schedule: isOneTime ? "one-time" : schedule,
        waitForPrevious: isOneTime ? false : waitForPrevious,
        initialParams: params,
        runOnce: isOneTime,
      };

      // Only include timing fields if they are enabled and not one-time
      if (!isOneTime) {
        if (timingMode === 'startTime' && startTime) {
          payload.startTime = startTime;
        } else if (timingMode === 'startDelay') {
          payload.startDelay = startDelay;
        } else if (timingMode === 'phaseSec') {
          payload.phaseSec = phaseSec;
        }
      }

      await apiClient.post("/api/workflows/recurring-workflows", payload);

      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error(
        `Failed to create ${isOneTime ? "one-time" : "recurring"} workflow:`,
        error
      );
      openAlert(
        error.message ||
          `Failed to create ${isOneTime ? "one-time" : "recurring"} workflow`,
        { title: "Error", confirmColor: "error" }
      );
    }
  };

  const handleClose = () => {
    // Reset form
    setSelectedWorkflowType("");
    setSelectedTestDataIndex(0);
    setSchedule("@every 1h");
    setWaitForPrevious(true);
    setInitialParams("{}");
    setStartTime("");
    setStartDelay(0);
    setPhaseSec(0);
    setTimingMode('none');
    onClose();
  };

  const handleTestDataChange = (e: SelectChangeEvent) => {
    const index = parseInt(e.target.value);
    setSelectedTestDataIndex(index);

    // Update initial params with selected test data
    const workflowType = workflowTypes.find((wt) => wt.name === selectedWorkflowType);
    const testData = workflowType?.testData?.[index];
    if (testData) {
      setInitialParams(JSON.stringify(testData.data, null, 2));
    }
  };

  const handleTimingModeChange = (mode: 'none' | 'startTime' | 'startDelay' | 'phaseSec') => {
    setTimingMode(mode);
    
    // Set default values when enabling modes
    if (mode === 'startTime' && !startTime) {
      const now = new Date();
      const localDateTime = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(0, 16);
      setStartTime(localDateTime);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isOneTime ? "Create One-time Workflow" : "Create Recurring Workflow"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Autocomplete
            options={workflowTypes}
            getOptionLabel={(option) => option.name}
            value={workflowTypes.find(wt => wt.name === selectedWorkflowType) || null}
            onChange={(_, newValue) => {
              const name = newValue?.name || "";
              setSelectedWorkflowType(name);
              setSelectedTestDataIndex(0);
              const testData = newValue?.testData?.[0];
              setInitialParams(testData ? JSON.stringify(testData.data, null, 2) : "{}");
            }}
            renderInput={(params) => <TextField {...params} label="Workflow Type" />}
            fullWidth
          />

          {/* Test Data Selector */}
          {selectedWorkflowType && (() => {
            const selectedWorkflow = workflowTypes.find((wt) => wt.name === selectedWorkflowType);
            return selectedWorkflow && selectedWorkflow.testData && selectedWorkflow.testData.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel>Test Data</InputLabel>
                <Select
                  value={selectedTestDataIndex.toString()}
                  onChange={handleTestDataChange}
                  label="Test Data"
                >
                  {selectedWorkflow.testData.map((testData, index) => (
                    <MenuItem key={index} value={index.toString()}>
                      {testData.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null;
          })()}

          {/* Show description of selected test data */}
          {selectedWorkflowType && (() => {
            const selectedWorkflow = workflowTypes.find((wt) => wt.name === selectedWorkflowType);
            const testData = selectedWorkflow?.testData?.[selectedTestDataIndex];
            return testData && testData.description ? (
              <Box sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" color="text.secondary">
                  {testData.description}
                </Typography>
              </Box>
            ) : null;
          })()}

          {!isOneTime && (
            <TextField
              label="Schedule"
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              fullWidth
              helperText='Use cron expressions (e.g., "0 * * * *") or intervals (e.g., "@every 5m")'
            />
          )}

          {!isOneTime && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={waitForPrevious}
                  onChange={(e) => setWaitForPrevious(e.target.checked)}
                />
              }
              label="Wait for previous run to complete"
            />
          )}

          {!isOneTime && (
            <FormControl fullWidth>
              <Typography variant="subtitle2" gutterBottom>
                Timing Options
              </Typography>
              <RadioGroup
                value={timingMode}
                onChange={(e) => {
                  const mode = e.target.value as 'none' | 'startTime' | 'startDelay' | 'phaseSec';
                  handleTimingModeChange(mode);
                }}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio />}
                  label="No timing constraints"
                />
                <FormControlLabel
                  value="startTime"
                  control={<Radio />}
                  label="Set specific start time"
                />
                <FormControlLabel
                  value="startDelay"
                  control={<Radio />}
                  label="Set start delay (seconds)"
                />
                <FormControlLabel
                  value="phaseSec"
                  control={<Radio />}
                  label="Set phase offset (seconds)"
                />
              </RadioGroup>
            </FormControl>
          )}

          {!isOneTime && timingMode === 'startTime' && (
            <TextField
              label="Start Time (UTC)"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="⚠️ Please enter time in UTC timezone"
            />
          )}

          {!isOneTime && timingMode === 'startDelay' && (
            <TextField
              label="Start Delay (seconds)"
              type="number"
              value={startDelay}
              onChange={(e) => setStartDelay(Number(e.target.value))}
              fullWidth
              helperText="Delay from now (0 = start immediately)"
              slotProps={{ htmlInput: { min: 0 } }}
            />
          )}

          {!isOneTime && timingMode === 'phaseSec' && (
            <TextField
              label="Phase Offset (seconds)"
              type="number"
              value={phaseSec}
              onChange={(e) => setPhaseSec(Number(e.target.value))}
              fullWidth
              helperText="Phase offset for interval schedules (must be less than interval duration)"
              slotProps={{ htmlInput: { min: 0 } }}
            />
          )}

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Initial Parameters (JSON)
            </Typography>
            <MonacoJsonEditor
              value={initialParams}
              onChange={setInitialParams}
              height={300}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!selectedWorkflowType}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Confirmation Dialog */}
    <ConfirmationDialog />
  </>
  );
};

export default CreateRecurringWorkflowModal;